import dayjs from "dayjs";
import {PublicFormField, PublicRegistrationBulkCsvImportResult, PublicRegistrationBulkRowValues, PublicRegistrationOption, bulkParticipantColumnMap} from "./types";
import {
    buildInitialBulkRegistrationRowValues,
    normalizePublicFormFieldType,
    normalizePublicRegistrationOptionQuestionType,
    replaceLabelValue
} from "./publicRegistrationModel";

type BulkCsvDelimiter = "," | ";" | "\t";

type BulkCsvParseCandidate = {
    dataStartIndex: number;
    headerIndex: number;
    recognizedHeaderCount: number;
    rows: string[][];
};

const bulkCsvDelimiters: BulkCsvDelimiter[] = [",", ";", "\t"];
const bulkCsvDataStartMarker = "EVENTFLOW_DATA_START";
const bulkCsvMetadataMarkers = [
    "EVENTFLOW_DESCRIPTION",
    "EVENTFLOW_TYPE",
    "EVENTFLOW_ALLOWED_VALUES",
    "EVENTFLOW_DEPENDENCY",
    "EVENTFLOW_EXAMPLE",
    bulkCsvDataStartMarker
];

const parseCsvRows = (text: string, delimiter: BulkCsvDelimiter): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = "";
    let inQuotes = false;

    const pushRow = () => {
        row.push(cell);
        rows.push(row);
        row = [];
        cell = "";
    };

    for (let index = 0; index < text.length; index += 1) {
        const char = text[index];
        const next = text[index + 1];

        if (char === "\"") {
            if (inQuotes && next === "\"") {
                cell += "\"";
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === delimiter && !inQuotes) {
            row.push(cell);
            cell = "";
        } else if ((char === "\n" || char === "\r") && !inQuotes) {
            if (char === "\r" && next === "\n") {
                index += 1;
            }
            pushRow();
        } else {
            cell += char;
        }
    }

    if (cell.length > 0 || row.length > 0) {
        pushRow();
    }

    return rows;
};

const normalizeBulkCsvMarker = (value: string): string => value
    .trim()
    .replace(/\*/g, "")
    .replace(/\\/g, "")
    .toUpperCase();

const isBulkCsvDataStartRow = (row: string[]): boolean => {
    const firstCell = normalizeBulkCsvMarker(row[0] ?? "");
    return firstCell === bulkCsvDataStartMarker || firstCell.startsWith(`${bulkCsvDataStartMarker} `);
};

const isBulkCsvMetadataRow = (row: string[]): boolean => {
    const firstCell = (row[0] ?? "")
        .trim()
        .replace(/\\/g, "")
        .toUpperCase();
    return bulkCsvMetadataMarkers.some(marker => firstCell.startsWith(`**${marker}**`));
};

const isRecognizedBulkCsvHeader = (header: string): boolean => {
    const normalizedHeader = header.trim();
    return !!bulkParticipantColumnMap[normalizedHeader]
        || /^answer:.+$/i.test(normalizedHeader)
        || matchBulkActivityColumn(normalizedHeader) !== undefined;
};

const buildBulkCsvParseCandidate = (
    text: string,
    delimiter: BulkCsvDelimiter
): BulkCsvParseCandidate => {
    const rows = parseCsvRows(text, delimiter);
    let headerIndex = -1;
    let recognizedHeaderCount = 0;

    rows.forEach((row, index) => {
        const score = row.filter(isRecognizedBulkCsvHeader).length;
        if (score > recognizedHeaderCount) {
            headerIndex = index;
            recognizedHeaderCount = score;
        }
    });

    const markerIndex = rows.findIndex(isBulkCsvDataStartRow);
    const dataStartIndex = markerIndex > headerIndex
        ? markerIndex + 1
        : headerIndex + 1;

    return {
        dataStartIndex,
        headerIndex,
        recognizedHeaderCount,
        rows
    };
};

const prepareBulkCsvText = (text: string): {
    content: string;
    preferredDelimiter?: BulkCsvDelimiter;
} => {
    const withoutBom = text.replace(/^\uFEFF/, "");
    const firstLineEnd = withoutBom.search(/[\r\n]/);
    const firstLine = (firstLineEnd >= 0 ? withoutBom.slice(0, firstLineEnd) : withoutBom).trim();
    const separatorMatch = /^sep=(,|;|\t)$/i.exec(firstLine);
    const preferredDelimiter = separatorMatch?.[1] as BulkCsvDelimiter | undefined;

    if (!preferredDelimiter) {
        return {content: withoutBom};
    }

    let contentStart = firstLineEnd;
    while (contentStart >= 0 && /[\r\n]/.test(withoutBom[contentStart] ?? "")) {
        contentStart += 1;
    }

    return {
        content: contentStart >= 0 ? withoutBom.slice(contentStart) : "",
        preferredDelimiter
    };
};

const detectBulkCsvParseCandidate = (text: string): BulkCsvParseCandidate => {
    const prepared = prepareBulkCsvText(text);
    const delimiters = prepared.preferredDelimiter
        ? [prepared.preferredDelimiter, ...bulkCsvDelimiters.filter(value => value !== prepared.preferredDelimiter)]
        : bulkCsvDelimiters;

    return delimiters
        .map(delimiter => buildBulkCsvParseCandidate(prepared.content, delimiter))
        .reduce((best, candidate) => candidate.recognizedHeaderCount > best.recognizedHeaderCount
            ? candidate
            : best);
};

const parseBulkAnswerValue = (field: PublicFormField, rawValue: string): unknown => {
    const value = rawValue.trim();
    const fieldType = normalizePublicFormFieldType(field.type);

    if (fieldType === "Checkbox") {
        return ["true", "1", "yes", "y", "da", "x"].includes(value.toLowerCase());
    }

    if (fieldType === "MultiSelect") {
        return value ? value.split(",").map(item => item.trim()).filter(Boolean) : [];
    }

    if (fieldType === "Number") {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : undefined;
    }

    if (fieldType === "Date") {
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : undefined;
    }

    return value;
};

const matchBulkActivityColumn = (header: string): number | undefined => {
    const match = /^activity:(\d+)(?::.*)?$/i.exec(header.trim());
    const optionId = Number(match?.[1]);
    return Number.isInteger(optionId) && optionId > 0 ? optionId : undefined;
};

const isBulkActivitySelected = (rawValue: string): boolean =>
    ["true", "1", "yes", "y", "da", "x"].includes(rawValue.trim().toLowerCase());

const resolveBulkActivityChoiceId = (
    option: PublicRegistrationOption,
    rawValue: string
): number | undefined => {
    const normalizedValue = rawValue.trim().toLowerCase();
    if (!normalizedValue) {
        return undefined;
    }

    const choice = (option.choices ?? []).find(candidate => [
        String(candidate.id),
        candidate.value,
        candidate.labelHr,
        candidate.labelEn
    ].some(value => value?.trim().toLowerCase() === normalizedValue));
    return choice?.id;
};

const parseBulkActivityValue = (
    option: PublicRegistrationOption,
    rawValue: string
): { selected: boolean; answer?: unknown } => {
    const questionType = normalizePublicRegistrationOptionQuestionType(option.questionType);
    if (questionType === "YesNo") {
        return {selected: isBulkActivitySelected(rawValue)};
    }

    if (questionType === "SingleChoice") {
        const choiceId = resolveBulkActivityChoiceId(option, rawValue);
        return {selected: choiceId !== undefined, answer: choiceId};
    }

    if (questionType === "MultiChoice") {
        const choiceIds = rawValue
            .split(",")
            .map(value => resolveBulkActivityChoiceId(option, value))
            .filter((choiceId): choiceId is number => choiceId !== undefined);
        const uniqueChoiceIds = Array.from(new Set(choiceIds));
        return {selected: uniqueChoiceIds.length > 0, answer: uniqueChoiceIds};
    }

    const textValue = rawValue.trim();
    return {selected: !!textValue, answer: textValue};
};

export const parseBulkRegistrationCsv = (
    text: string,
    fields: PublicFormField[] | undefined,
    registrationOptions: PublicRegistrationOption[] | undefined = []
): PublicRegistrationBulkCsvImportResult => {
    const csvCandidate = detectBulkCsvParseCandidate(text);
    if (csvCandidate.recognizedHeaderCount === 0 || csvCandidate.headerIndex < 0) {
        return {
            rows: [],
            unknownColumns: [],
            unsupportedAnswerColumns: [],
            unsupportedActivityColumns: [],
            formatError: true
        };
    }

    if (csvCandidate.rows.slice(csvCandidate.headerIndex + 1).some(row =>
        isBulkCsvDataStartRow(row) && row.slice(1).some(value => value.trim() !== ""))) {
        return {
            rows: [],
            unknownColumns: [],
            unsupportedAnswerColumns: [],
            unsupportedActivityColumns: [],
            formatError: false,
            dataOnMarkerRow: true
        };
    }

    const headers = (csvCandidate.rows[csvCandidate.headerIndex] ?? []).map(header => header.trim());
    const activeFieldsByCode = new Map((fields ?? []).map(field => [field.code.trim(), field]));
    const registrationOptionsById = new Map((registrationOptions ?? []).map(option => [option.id, option]));
    const unknownColumns: string[] = [];
    const unsupportedAnswerColumns: string[] = [];
    const unsupportedActivityColumns: string[] = [];

    headers.forEach(header => {
        if (!header) {
            return;
        }

        if (bulkParticipantColumnMap[header]) {
            return;
        }

        if (header.startsWith("answer:")) {
            const fieldCode = header.slice("answer:".length).trim();
            if (!activeFieldsByCode.has(fieldCode)) {
                unsupportedAnswerColumns.push(header);
            }
            return;
        }

        const activityOptionId = matchBulkActivityColumn(header);
        if (activityOptionId !== undefined) {
            if (!registrationOptionsById.has(activityOptionId)) {
                unsupportedActivityColumns.push(header);
            }
            return;
        }

        unknownColumns.push(header);
    });

    const rows = csvCandidate.rows.slice(csvCandidate.dataStartIndex).reduce<PublicRegistrationBulkRowValues[]>((mappedRows, csvRow) => {
        if (isBulkCsvMetadataRow(csvRow) || csvRow.every(value => value.trim() === "")) {
            return mappedRows;
        }

        const rowValues = buildInitialBulkRegistrationRowValues(fields, registrationOptions);

        headers.forEach((header, index) => {
            const value = csvRow[index] ?? "";
            const participantKey = bulkParticipantColumnMap[header];

            if (participantKey) {
                rowValues[participantKey] = value.trim();
                return;
            }

            if (header.startsWith("answer:")) {
                const fieldCode = header.slice("answer:".length).trim();
                const field = activeFieldsByCode.get(fieldCode);
                if (field) {
                    rowValues.answers = rowValues.answers ?? {};
                    rowValues.answers[fieldCode] = parseBulkAnswerValue(field, value);
                }
                return;
            }

            const activityOptionId = matchBulkActivityColumn(header);
            const activityOption = activityOptionId === undefined
                ? undefined
                : registrationOptionsById.get(activityOptionId);
            if (activityOption) {
                const parsedActivity = parseBulkActivityValue(activityOption, value);
                if (normalizePublicRegistrationOptionQuestionType(activityOption.questionType) === "YesNo") {
                    if (parsedActivity.selected) {
                        rowValues.selectedOptionIds = [
                            ...(rowValues.selectedOptionIds ?? []),
                            activityOption.id
                        ];
                    }
                } else {
                    rowValues.activityAnswers = rowValues.activityAnswers ?? {};
                    rowValues.activityAnswers[String(activityOption.id)] = parsedActivity.answer;
                }
            }
        });

        mappedRows.push(rowValues);
        return mappedRows;
    }, []);

    return {
        rows,
        unknownColumns: Array.from(new Set(unknownColumns)),
        unsupportedAnswerColumns: Array.from(new Set(unsupportedAnswerColumns)),
        unsupportedActivityColumns: Array.from(new Set(unsupportedActivityColumns)),
        formatError: false
    };
};

type BulkCsvImportOutcome = {
    rows?: PublicRegistrationBulkRowValues[];
    notice: {
        type: "success" | "warning" | "error";
        message: string;
        description?: string;
    };
};

// Turns a parsed CSV into the rows to load (if any) and the participant-facing import notice.
export const describeBulkCsvImport = (
    result: PublicRegistrationBulkCsvImportResult,
    labels: Record<string, string>
): BulkCsvImportOutcome => {
    if (result.formatError) {
        return {notice: {type: "error", message: labels.bulkImportFormatError}};
    }

    if (result.dataOnMarkerRow) {
        return {notice: {type: "error", message: labels.bulkImportDataOnMarkerRow}};
    }

    if (result.rows.length === 0) {
        return {notice: {type: "warning", message: labels.bulkImportEmpty}};
    }

    const skippedColumns = [
        ...result.unknownColumns,
        ...result.unsupportedAnswerColumns,
        ...result.unsupportedActivityColumns
    ];

    return {
        rows: result.rows,
        notice: {
            type: skippedColumns.length > 0 ? "warning" : "success",
            message: replaceLabelValue(labels.bulkImportSuccess, "count", String(result.rows.length)),
            description: skippedColumns.length > 0
                ? `${labels.bulkImportSkippedColumns}: ${skippedColumns.join(", ")}`
                : undefined
        }
    };
};
